import type { ColorResampler } from "../color-resampler";
import { AbortException } from "../exception";
import type { ImageAdapter } from "../image-adapter";
import { createDeferred, type Deferred } from "../utils";
import type { DistortionProcessor } from "./DistortionProcessor";

/**
 * Reverse pixel mapping process scope.
 * @internal
 */
export interface ProcessScope<ResourceType> {
  /**
   * Deferred object which will be resolved when all image pixels are processed.
   */
  deferred: Deferred<void>;

  /**
   * Target image.
   */
  image: ImageAdapter<ResourceType>;

  /**
   * Color resampler.
   */
  resampler: ColorResampler;

  /**
   * Current X position, remembered between partial image pixels loops.
   */
  currentX: number;

  /**
   * Current Y position, remembered between partial image pixels loops.
   */
  currentY: number;

  /**
   * Start X position, remembered between partial image pixels loops.
   */
  startX: number;

  /**
   * End X position, remembered between partial image pixels loops.
   */
  endX: number;

  /**
   * End Y position, remembered between partial image pixels loops.
   */
  endY: number;
}

/**
 * Reverse Pixel Mapping processor.
 */
export class ReversePixelMappingProcessor implements DistortionProcessor {
  /**
   * ReversePixelMappingProcessor constructor.
   *
   * @param asyncTimeout Timeout for delaying further pixel processing.
   */
  constructor(
    private asyncTimeout: number,
    private abortSignal?: AbortSignal
  ) {}

  /**
   * Processes reverse pixel mapping.
   *
   * @param targetImage
   * @param resampler
   */
  async process<ResourceType>(
    targetImage: ImageAdapter<ResourceType>,
    resampler: ColorResampler
  ): Promise<ImageAdapter<ResourceType>> {
    const scope = this.createProcessScope(targetImage, resampler);
    this.doProcess(scope, this.asyncTimeout);
    await scope.deferred.promise;
    targetImage.commit();
    return targetImage;
  }

  /**
   * Creates scope for particular image processing.
   *
   * @param image
   * @param resampler
   * @private
   */
  private createProcessScope<ResourceType>(
    image: ImageAdapter<ResourceType>,
    resampler: ColorResampler
  ): ProcessScope<ResourceType> {
    const vp = image.getViewport();
    const startX = Math.floor(vp.x1);
    const endX = Math.floor(vp.x2);
    const endY = Math.floor(vp.y2);
    const currentX = startX;
    const currentY = Math.floor(vp.y1);
    return {
      deferred: createDeferred(),
      image,
      resampler,
      startX,
      endX,
      endY,
      currentX,
      currentY,
    };
  }

  /**
   * Processes copying of resampled colors of source image to target image.
   *
   * @param scope
   * @param asyncTimeout
   * @private
   */
  private doProcess<ResourceType>(
    scope: ProcessScope<ResourceType>,
    asyncTimeout: number
  ): void {
    if (this.abortSignal && this.abortSignal.aborted) {
      scope.deferred.reject(
        this.abortSignal.reason instanceof Error
          ? this.abortSignal.reason
          : new AbortException(String(this.abortSignal.reason))
      );
      return;
    }
    const startTime = Date.now();
    const { image, resampler, startX, endX, endY } = scope;
    let { currentX: x, currentY: y } = scope;

    while (y <= endY) {
      while (x <= endX) {
        image.setPixelColor(x, y, resampler.getResampledColor(x, y));
        x++;

        if (Date.now() - startTime >= asyncTimeout) {
          scope.currentX = x;
          scope.currentY = y;
          setTimeout(this.doProcess.bind(this, scope, asyncTimeout), 0);
          return;
        }
      }
      y++;
      x = startX;
    }

    scope.deferred.resolve();
  }
}
